// src/utils/msCalendarHelpers.js
// Helper functions for MS Calendar integration
// Handles date normalization, sync triggering, and webhook management

/**
 * Normalize Microsoft Graph API event dates to consistent ISO strings
 * Microsoft returns dates in two formats:
 * 1. Regular events: { dateTime: "2024-01-15T10:00:00.0000000", timeZone: "UTC" }
 * 2. All-day events: { dateTime: "2024-01-15T00:00:00.0000000", timeZone: "UTC" }
 * 
 * This function converts both to plain ISO strings that work reliably with Date()
 * 
 * @param {Object} msEvent - Raw Microsoft Graph API event
 * @returns {Object} - Normalized event with start/end as ISO strings
 */
export const normalizeMsEvent = (msEvent) => {
  if (!msEvent) return null;
  
  // Parse start date/time
  let startStr = null;
  if (msEvent.start) {
    if (typeof msEvent.start === 'string') {
      // Already a string
      startStr = msEvent.start;
    } else if (msEvent.start.dateTime) {
      // Microsoft Graph format: { dateTime: "...", timeZone: "..." }
      startStr = msEvent.start.dateTime;
      // Microsoft sometimes returns dates without timezone suffix
      // If the date doesn't end with Z, assume UTC (since we request UTC)
      if (startStr && !startStr.endsWith('Z') && !startStr.includes('+') && !startStr.includes('-', 10)) {
        startStr = startStr + 'Z';
      }
    }
  }
  
  // Parse end date/time
  let endStr = null;
  if (msEvent.end) {
    if (typeof msEvent.end === 'string') {
      endStr = msEvent.end;
    } else if (msEvent.end.dateTime) {
      endStr = msEvent.end.dateTime;
      if (endStr && !endStr.endsWith('Z') && !endStr.includes('+') && !endStr.includes('-', 10)) {
        endStr = endStr + 'Z';
      }
    }
  }
  
  // Validate dates
  const startDate = startStr ? new Date(startStr) : null;
  const endDate = endStr ? new Date(endStr) : null;
  
  // Check for Invalid Date
  const isValidStart = startDate && !isNaN(startDate.getTime());
  const isValidEnd = endDate && !isNaN(endDate.getTime());
  
  if (!isValidStart) {
    console.warn('MS Calendar: Invalid start date for event:', msEvent.id, msEvent.start);
  }
  if (!isValidEnd) {
    console.warn('MS Calendar: Invalid end date for event:', msEvent.id, msEvent.end);
  }
  
  return {
    id: msEvent.id,
    subject: msEvent.subject || 'Untitled Event',
    // Store as ISO strings for consistent parsing
    start: isValidStart ? startDate.toISOString() : null,
    end: isValidEnd ? endDate.toISOString() : null,
    location: msEvent.location?.displayName || '',
    categories: msEvent.categories || [],
    isAllDay: msEvent.isAllDay || false,
    organizer: msEvent.organizer?.emailAddress?.name || '',
    attendees: msEvent.attendees?.map(a => a.emailAddress?.name).filter(Boolean) || [],
    // Store original timezone info for debugging
    _originalTimeZone: {
      start: msEvent.start?.timeZone,
      end: msEvent.end?.timeZone
    }
  };
};

/**
 * Normalize an array of MS Calendar events
 * Filters out events with invalid dates
 * 
 * @param {Array} msEvents - Array of raw Microsoft Graph API events
 * @returns {Array} - Array of normalized events (invalid events filtered out)
 */
export const normalizeMsEvents = (msEvents) => {
  if (!Array.isArray(msEvents)) return [];
  
  return msEvents
    .map(normalizeMsEvent)
    .filter(event => event && event.start && event.end);
};

/**
 * Check if an MS Calendar event falls on a specific date
 * Handles timezone conversion to local date
 * 
 * @param {Object} event - Normalized MS Calendar event
 * @param {string} dateStr - Date string in YYYY-MM-DD format
 * @returns {boolean} - True if event occurs on the given date
 */
export const msEventFallsOnDate = (event, dateStr) => {
  if (!event || !event.start || !dateStr) return false;
  
  try {
    const eventStart = new Date(event.start);
    const eventEnd = event.end ? new Date(event.end) : eventStart;
    
    // Get local date strings for comparison
    const eventStartDate = eventStart.toISOString().split('T')[0];
    const eventEndDate = eventEnd.toISOString().split('T')[0];
    
    // For multi-day events, check if dateStr falls within range
    return dateStr >= eventStartDate && dateStr <= eventEndDate;
  } catch (error) {
    console.error('Error checking event date:', error, event);
    return false;
  }
};

/**
 * Filter MS Calendar events to a specific date range
 * 
 * @param {Array} events - Array of normalized MS Calendar events
 * @param {Date} startDate - Range start date
 * @param {Date} endDate - Range end date
 * @returns {Array} - Filtered events within the range
 */
export const filterMsEventsByDateRange = (events, startDate, endDate) => {
  if (!Array.isArray(events)) return [];
  
  const rangeStart = startDate.toISOString().split('T')[0];
  const rangeEnd = endDate.toISOString().split('T')[0];
  
  return events.filter(event => {
    if (!event || !event.start) return false;
    
    try {
      const eventStartDate = new Date(event.start).toISOString().split('T')[0];
      const eventEndDate = event.end 
        ? new Date(event.end).toISOString().split('T')[0]
        : eventStartDate;
      
      // Event overlaps with range if:
      // Event starts before range ends AND Event ends after range starts
      return eventStartDate <= rangeEnd && eventEndDate >= rangeStart;
    } catch (error) {
      return false;
    }
  });
};

/**
 * Check if webhook subscription is expired or expiring soon
 * 
 * @param {Object} subscription - Webhook subscription object
 * @param {number} thresholdHours - Hours before expiry to consider "expiring soon" (default: 12)
 * @returns {Object} - { isExpired, isExpiringSoon, hoursRemaining }
 */
export const checkWebhookExpiry = (subscription, thresholdHours = 12) => {
  if (!subscription || !subscription.expirationDateTime) {
    return { isExpired: true, isExpiringSoon: true, hoursRemaining: 0 };
  }
  
  try {
    const expirationDate = new Date(subscription.expirationDateTime);
    const now = new Date();
    const msRemaining = expirationDate.getTime() - now.getTime();
    const hoursRemaining = msRemaining / (1000 * 60 * 60);
    
    return {
      isExpired: hoursRemaining <= 0,
      isExpiringSoon: hoursRemaining > 0 && hoursRemaining < thresholdHours,
      hoursRemaining: Math.max(0, hoursRemaining)
    };
  } catch (error) {
    console.error('Error checking webhook expiry:', error);
    return { isExpired: true, isExpiringSoon: true, hoursRemaining: 0 };
  }
};

/**
 * Get event duration in seconds
 * Returns 0 for invalid events or all-day events
 * 
 * @param {Object} event - Normalized MS Calendar event
 * @returns {number} - Duration in seconds
 */
export const getMsEventDuration = (event) => {
  if (!event || !event.start || !event.end || event.isAllDay) return 0;
  
  try {
    const startDate = new Date(event.start);
    const endDate = new Date(event.end);
    const durationMs = endDate.getTime() - startDate.getTime();
    
    // Validate duration is positive and reasonable (max 24 hours)
    if (durationMs > 0 && durationMs <= 24 * 60 * 60 * 1000) {
      return Math.floor(durationMs / 1000);
    }
    return 0;
  } catch (error) {
    return 0;
  }
};

/**
 * Format MS event for display in calendar
 * 
 * @param {Object} msEvent - Normalized MS Calendar event
 * @param {string} dateStr - The date being rendered (YYYY-MM-DD)
 * @returns {Object} - Formatted event object for calendar display
 */
export const formatMsEventForCalendar = (msEvent, dateStr) => {
  if (!msEvent) return null;
  
  const startDate = msEvent.start ? new Date(msEvent.start) : null;
  const endDate = msEvent.end ? new Date(msEvent.end) : null;
  
  return {
    id: `ms-${msEvent.id}`,
    eventType: 'msCalendar',
    title: msEvent.subject || 'MS Calendar Event',
    description: msEvent.location,
    category: 'msCalendar',
    color: 'bg-indigo-100 text-indigo-800 border-indigo-300',
    isAllDay: msEvent.isAllDay || false,
    startDate: startDate ? startDate.toISOString().split('T')[0] : dateStr,
    endDate: endDate ? endDate.toISOString().split('T')[0] : dateStr,
    startTime: startDate && !msEvent.isAllDay ? startDate.toTimeString().slice(0, 5) : null,
    endTime: endDate && !msEvent.isAllDay ? endDate.toTimeString().slice(0, 5) : null,
    status: 'synced',
    sourceId: msEvent.id,
    sourceTable: 'ms_calendar',
    source: 'microsoft',
    organizer: msEvent.organizer,
    attendees: msEvent.attendees,
    categories: msEvent.categories
  };
};

// ============================================================================
// GRAPH API CALENDAR MANAGEMENT — Idempotent calendar creation
// ============================================================================

/**
 * Generic Graph API request with structured error handling.
 * Throws with { code, message, status } on non-2xx responses.
 *
 * @param {string} url - Full Graph API URL
 * @param {string} accessToken - Bearer token
 * @param {object} [options] - Fetch options (method, body, etc.)
 * @returns {Promise<object>} Parsed JSON response
 */
export const graphFetch = async (url, accessToken, options = {}) => {
  const { method = 'GET', body } = options;
  const headers = {
    Authorization: `Bearer ${accessToken}`,
    ...(body ? { 'Content-Type': 'application/json' } : {})
  };

  const response = await fetch(url, {
    method,
    headers,
    ...(body ? { body: JSON.stringify(body) } : {})
  });

  if (!response.ok) {
    let errorData = {};
    try { errorData = await response.json(); } catch (_) { /* empty */ }
    const graphError = errorData.error || {};
    const err = new Error(graphError.message || `Graph API ${method} ${url} failed (HTTP ${response.status})`);
    err.code = graphError.code || `HTTP_${response.status}`;
    err.status = response.status;
    err.graphError = graphError;
    throw err;
  }

  // 204 No Content (e.g. DELETE) returns no body
  if (response.status === 204) return null;
  return response.json();
};

/**
 * List all calendars for the authenticated user.
 *
 * @param {string} accessToken - Microsoft Graph bearer token
 * @returns {Promise<Array<{id: string, name: string}>>} Calendar list
 */
export const getCalendars = async (accessToken) => {
  const allCalendars = [];
  let url = 'https://graph.microsoft.com/v1.0/me/calendars?$select=id,name&$top=50';

  while (url) {
    const data = await graphFetch(url, accessToken);
    const page = data.value || [];
    allCalendars.push(...page);
    // Microsoft Graph uses '@odata.nextLink' for pagination
    url = data['@odata.nextLink'] || null;
  }

  console.log(`[MS Calendar] getCalendars: fetched ${allCalendars.length} calendars (all pages)`);
  return allCalendars;
};

/**
 * Create a new calendar in Outlook.
 *
 * @param {string} accessToken - Microsoft Graph bearer token
 * @param {string} name - Calendar display name
 * @param {object} [extraFields] - Optional fields (e.g. { color: 'lightBlue' })
 * @returns {Promise<{id: string, name: string}>} Created calendar
 */
export const createCalendar = async (accessToken, name, extraFields = {}) => {
  return graphFetch(
    'https://graph.microsoft.com/v1.0/me/calendars',
    accessToken,
    { method: 'POST', body: { name, ...extraFields } }
  );
};

/**
 * Normalize a calendar name for comparison.
 * Handles case, leading/trailing whitespace, and non-breaking spaces (U+00A0).
 * @param {string} s - Raw calendar name
 * @returns {string} Normalized lowercase trimmed string
 */
const normCalName = (s) => (s || '').replace(/\u00A0/g, ' ').trim().toLowerCase();

/**
 * Ensure a calendar with the given name exists in Outlook.
 * Idempotent: lists all calendars first (with pagination), reuses existing match
 * (normalized case-insensitive + whitespace-safe), only creates if not found.
 * Handles 409 ErrorFolderExists gracefully (race condition fallback).
 *
 * THIS IS THE ONLY FUNCTION THAT SHOULD EVER CREATE CALENDARS.
 * Never call createCalendar() directly from App.js or other modules.
 *
 * @param {string} accessToken - Microsoft Graph bearer token
 * @param {string} name - Desired calendar name
 * @param {object} [extraFields] - Optional fields for creation (e.g. { color: 'lightBlue' })
 * @returns {Promise<{id: string, name: string, created: boolean}>}
 */
export const ensureCalendar = async (accessToken, name, extraFields = {}) => {
  // Step 1: List ALL existing calendars (paginated) and find by normalized name
  console.log(`[MS Calendar] ensureCalendar: looking for "${name}"...`);
  const calendars = await getCalendars(accessToken);
  const nameNorm = normCalName(name);
  const matches = calendars.filter(c => normCalName(c.name) === nameNorm);

  if (matches.length > 0) {
    if (matches.length > 1) {
      console.warn(`[MS Calendar] ensureCalendar: Found ${matches.length} calendars matching "${name}" — using first (id: ${matches[0].id})`);
    }
    console.log(`[MS Calendar] ensureCalendar: MATCH FOUND "${matches[0].name}" (id: ${matches[0].id}) — no POST needed`);
    return { id: matches[0].id, name: matches[0].name, created: false };
  }

  // Step 2: Not found — create it
  console.log(`[MS Calendar] ensureCalendar: NO match in ${calendars.length} calendars — creating "${name}"...`);
  try {
    const created = await createCalendar(accessToken, name, extraFields);
    console.log(`[MS Calendar] ensureCalendar: CREATED "${created.name}" (id: ${created.id})`);
    return { id: created.id, name: created.name, created: true };
  } catch (err) {
    // Step 3: Handle race condition — 409 means it was created between list and POST
    if (err.status === 409 || err.code === 'ErrorFolderExists') {
      console.warn(`[MS Calendar] ensureCalendar: 409 race condition for "${name}" — re-fetching all pages...`);
      const retryCalendars = await getCalendars(accessToken);
      const retryMatch = retryCalendars.find(c => normCalName(c.name) === nameNorm);
      if (retryMatch) {
        console.log(`[MS Calendar] ensureCalendar: Found on retry "${retryMatch.name}" (id: ${retryMatch.id})`);
        return { id: retryMatch.id, name: retryMatch.name, created: false };
      }
      // Extremely unlikely: 409 but still can't find it
      throw new Error(`Calendar "${name}" reported as existing (409) but not found on retry`);
    }
    throw err;
  }
};

/**
 * Find a calendar by name (normalized: case-insensitive, whitespace-safe)
 * from a pre-fetched list.
 * Utility for batch operations that already have the calendar list.
 *
 * @param {Array<{id: string, name: string}>} calendars - Pre-fetched calendar list
 * @param {string} name - Calendar name to find
 * @returns {{id: string, name: string}|null} Matched calendar or null
 */
export const findCalendarByName = (calendars, name) => {
  if (!Array.isArray(calendars) || !name) return null;
  const nameNorm = normCalName(name);
  const matches = calendars.filter(c => normCalName(c.name) === nameNorm);
  if (matches.length > 1) {
    console.warn(`[MS Calendar] findCalendarByName: ${matches.length} calendars match "${name}" — returning first`);
  }
  return matches[0] || null;
};

export default {
  normalizeMsEvent,
  normalizeMsEvents,
  msEventFallsOnDate,
  filterMsEventsByDateRange,
  checkWebhookExpiry,
  getMsEventDuration,
  formatMsEventForCalendar,
  // Graph API helpers
  graphFetch,
  getCalendars,
  createCalendar,
  ensureCalendar,
  findCalendarByName
};
